import type { ConfigField } from "@/api.js"
import { Input } from "@/components/ui/input.js"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.js"
import { Slider } from "@/components/ui/slider.js"
import { Switch } from "@/components/ui/switch.js"

export type FieldValue = string | number | boolean

/**
 * One control, rendered from what a rule or a provider **declared about
 * itself** — and nothing but the control: the editor lays out the label and
 * the help around it, the same way for a declared parameter as for a setting
 * every rule has.
 *
 * The panel deliberately does not know that `gus-business-age` has a
 * threshold, in the same way it does not know that `krd` has a cache window. A
 * form hardcoded per integration is an edit in the front end for every source
 * added after it — the shape this repository refuses for manifests, reason
 * codes, health and the entity screen, and there is no reason for
 * configuration to be the exception.
 */
export function FieldControl({
  id,
  field,
  value,
  onChange,
}: {
  id: string
  field: ConfigField
  value: unknown
  onChange: (next: FieldValue) => void
}) {
  if (field.type === "select") {
    return (
      <Select value={asString(value, field)} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full max-w-md" aria-label={field.label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(field.options ?? []).map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  if (field.type === "boolean") {
    const on = asBoolean(value, field)
    return (
      // A switch, not a hand-rolled pair of buttons: it carries `role="switch"`
      // and its state to assistive technology for free. The word travels with
      // it, because these screens get screenshotted and a thumb position on its
      // own is a poor thing to argue from.
      <div className="flex h-8 items-center gap-2.5">
        <Switch id={id} checked={on} aria-label={field.label} onCheckedChange={onChange} />
        <span className="text-[13px] text-muted-foreground">{on ? "Tak" : "Nie"}</span>
      </div>
    )
  }

  if (isSlider(field)) {
    return (
      // The readout rides beside the slider rather than above it, because a
      // slider without its number is a gesture and not a setting.
      <div className="flex h-8 max-w-md items-center gap-4">
        <Slider
          value={[asNumber(value, field)]}
          min={field.min}
          max={field.max}
          step={stepOf(field)}
          aria-label={field.label}
          onValueChange={([next]) => onChange(next ?? asNumber(value, field))}
        />
        <span className="w-16 shrink-0 text-right text-[13px] font-medium tabular-nums text-foreground">
          {readout(field, value)}
        </span>
      </div>
    )
  }

  if (field.type === "integer" || field.type === "number") {
    return <NumberBox id={id} field={field} value={value} onChange={onChange} />
  }

  return (
    <Input
      id={id}
      className="max-w-md"
      aria-label={field.label}
      value={asString(value, field)}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

/** An unbounded number: a box, because a slider with no ends lies about range. */
function NumberBox({
  id,
  field,
  value,
  onChange,
}: {
  id: string
  field: ConfigField
  value: unknown
  onChange: (next: FieldValue) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <Input
        id={id}
        type="number"
        inputMode={field.type === "integer" ? "numeric" : "decimal"}
        aria-label={field.label}
        className="w-28 tabular-nums"
        value={String(asNumber(value, field))}
        {...(field.min === undefined ? {} : { min: field.min })}
        {...(field.max === undefined ? {} : { max: field.max })}
        step={field.type === "integer" ? 1 : "any"}
        onChange={(event) => {
          const parsed = Number(event.target.value)
          // Garbage falls back to what the field declares, never to zero: zero is
          // a decision, and a half-typed number must not silently make it.
          onChange(Number.isFinite(parsed) ? parsed : numberDefault(field))
        }}
      />
      {field.unit !== undefined && (
        <span className="text-[13px] text-muted-foreground">{field.unit}</span>
      )}
    </div>
  )
}

/**
 * A field's current value as one short phrase — `12 mies.`, `Tak`, an option's
 * label — for the readout beside a slider and for wherever a setting is quoted
 * without its control, so the two can never disagree about what is set.
 */
export function readout(field: ConfigField, value: unknown): string {
  switch (field.type) {
    case "boolean":
      return asBoolean(value, field) ? "Tak" : "Nie"
    case "select": {
      const chosen = asString(value, field)
      return field.options?.find((option) => option.value === chosen)?.label ?? chosen
    }
    case "string":
      return asString(value, field)
    case "integer":
    case "number": {
      const number = asNumber(value, field).toLocaleString("pl-PL")
      return field.unit === undefined ? number : `${number} ${field.unit}`
    }
  }
}

/**
 * A bounded number gets a slider, because the bounds are the whole point: the
 * person setting it can see where the value sits between them. An unbounded one
 * gets a box, because a slider with no ends is a lie about the range.
 */
function isSlider(field: ConfigField): field is ConfigField & { min: number; max: number } {
  return (
    (field.type === "integer" || field.type === "number") &&
    field.min !== undefined &&
    field.max !== undefined
  )
}

/** Enough stops to be usable, coarse enough that the readout stays legible. */
function stepOf(field: ConfigField & { min: number; max: number }): number {
  if (field.type === "integer") return 1
  const span = field.max - field.min
  return span <= 1 ? 0.05 : span <= 10 ? 0.1 : 1
}

function numberDefault(field: ConfigField): number {
  return typeof field.default === "number" ? field.default : 0
}

function asNumber(value: unknown, field: ConfigField): number {
  return typeof value === "number" && Number.isFinite(value) ? value : numberDefault(field)
}

function asBoolean(value: unknown, field: ConfigField): boolean {
  return typeof value === "boolean" ? value : field.default === true
}

function asString(value: unknown, field: ConfigField): string {
  if (typeof value === "string") return value
  return typeof field.default === "string" ? field.default : ""
}
